import {fullDate, ymd} from '../../src/utils/dates.ts';
import type {ContentQueryItem, ContentSourceData} from '../../types/types.ts';
import type {ContentAdapter} from '../adapters/content-adapter.ts';
import {LoadResourceError} from '../lib/errors.ts';
import type {Store} from '../lib/store.ts';
import type {ReaderListing} from '../pages/reader/reader-listing.ts';
import {loadContentSource} from '../services/content.ts';
import type {Article} from './article.ts';
import {ReaderEdition} from './reader-edition.ts';

export class ContentSourceEdition extends ReaderEdition {
	public contentSource!: ContentSourceData;
	readonly sourceId: string;
	readonly queryId: string;
	readonly date: string;
	readonly tag: string;
	readonly articleId: string;
	public readonly editionType = 'content-source';
	public readonly productType = 'feed';
	public query?: ContentQueryItem;

	constructor(
		sourceId: string,
		queryId: string,
		date: string,
		tag: string,
		articleId: string,
		adapter: ContentAdapter
	) {
		super(adapter);
		this.id = `${sourceId}-${queryId}`;
		this.sourceId = sourceId;
		this.queryId = queryId;
		this.productName = sourceId;
		this.date = date;
		this.tag = tag;
		this.articleId = articleId;
		this.publishDate = new Date(date).toISOString();
		this.title = fullDate(this.publishDate, this.locale);
	}

	async loadContentSource() {
		const response = await loadContentSource(this.sourceId);
		if (response.status !== 200) {
			throw new LoadResourceError('Error loading content source', response);
		}
		this.contentSource = response.body;
		this.query = this.contentSource.attributes.queries.find(({id}) => id === this.queryId);
		let productTitle = `${this.contentSource.attributes.title}: ${this.query?.title || ''}`;
		if (this.tag) {
			productTitle += ` / ${this.tag}`;
		}
		this.productTitle = productTitle;
		this.label = `${this.query?.title || ''} (${this.contentSource.attributes.title})`;
		if (this.query?.title) {
			this.productName = this.query.title;
			this.editionName = this.query.title;
		}
	}

	async loadArticleList(store: Store) {
		const items = await this.adapter.loadArticleList(this, store);
		const articleIds = items.map(({data}) => data.id);
		const articlesData = items.map(({data}) => data);
		await store.loadServerStatesById(articleIds);

		await articlesData.reduce(async (acc, curr) => {
			await acc;
			await this.adapter.ensurePublishedAt(curr);
		}, Promise.resolve());

		const timezone = this.contentSource.attributes.timezone;
		this.articles = this.adapter.formatListItems(articlesData, 'time', this.date, store, undefined, timezone);
		return this.articles;
	}

	loadFullArticle(
		article: Article,
		groupBy: string,
		sortOrder: number | undefined,
		store: Store
	): Promise<Article | null> {
		return this.adapter.loadItem(article, groupBy, sortOrder, store);
	}

	loadRelatedArticles(article: Article) {
		return this.adapter.loadRelatedArticles(article);
	}

	getSection(listing: ReaderListing): string {
		return listing.item.section;
	}

	getPath() {
		return `/feed/${this.sourceId}-${this.queryId}`;
	}

	getArticleUrl(listing: ReaderListing, pageNumber: number) {
		let basePath = `${this.getPath()}/${listing.id}`;
		if (pageNumber && pageNumber > 1) {
			basePath += `/${pageNumber}`;
		}

		return `${basePath}?date=${ymd(listing.item.publishedAt, this.timezone)}`;
	}

	getUrl(date: Date = new Date(this.date)) {
		return `${this.getPath()}?date=${ymd(date, this.timezone)}`;
	}
}
